import { injectable } from "inversify";
import IValidationService from "./IValidationService";
import FileValidator from 'features/validation/validators/FileValidator';
import { inversifyContainer } from "infrastructure/ioc/ioc.container";
import { IocTypes } from "infrastructure/ioc/ioc.types";

@injectable()
export default class ValidationService implements IValidationService {
      /**
     * 
     * @param pValue
     * @param pValidateFalse validuje true/false (null ignoruje)
     */
	public validateCheckBox(pValue: boolean, pValidateFalse: boolean = false): boolean {
		if (pValidateFalse && pValue === false) {
			return pValue;
		}

		if (pValue === null ||
			pValue === undefined) {
			return false;
		}

		return true;
	}

	public validateString(pValue: string): boolean {
		if (pValue === "" ||
			pValue === null ||
			pValue === undefined) {
			return false;
		}

		return true;
	}

	public validateNumber(pValue: number, pZeroIsValid: boolean = true): boolean {
		if (!pZeroIsValid && pValue === 0)
			return false;

		if (pValue === null ||
			pValue === undefined ||
			Number.isNaN(pValue)) {
			return false;
		}

		return true;
	}

	public validateDateNotInPast(pValue: any): boolean {
		if (pValue === null ||
			pValue === undefined ||
			pValue === "") {
			return true;
		}

		let date: Date;

		if (pValue instanceof Date) {
			date = new Date(pValue.getFullYear(), pValue.getMonth(), pValue.getDate());
		}
		else if (typeof pValue === "object" && typeof pValue.toDate === "function") {
			const momentDate = pValue.toDate() as Date;
			date = new Date(momentDate.getFullYear(), momentDate.getMonth(), momentDate.getDate());
		}
		else {
			const stringValue = String(pValue);
			const isoDateMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

			if (isoDateMatch) {
				date = new Date(Number(isoDateMatch[1]), Number(isoDateMatch[2]) - 1, Number(isoDateMatch[3]));
			}
			else {
				const parsed = new Date(stringValue);
				if (Number.isNaN(parsed.getTime())) {
					return false;
				}
				date = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
			}
		}

		if (Number.isNaN(date.getTime())) {
			return false;
		}

		const now = new Date();
		const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

		return date.getTime() >= today.getTime();
	}

	public validateCustomCheckboxes<T, U>(pObjectForValidation: T, pResultObject: U, pExclude: Array<keyof T> = []): U {
		const validateObjKeys = Object.keys(pObjectForValidation) as Array<keyof T>;

		for (let key of validateObjKeys) {
			let propertyType = typeof pObjectForValidation[key];
			let propertyValidName = key + "Valid";

			// Pokud je properta vyloučena, tak ji přeskočím
			if (pExclude.length > 0) {
				if (pExclude.filter(f => f === key)[0] !== undefined) continue;
			}

			if (propertyType === "boolean" || propertyType === "object") {
				if (Object.prototype.hasOwnProperty.call(pResultObject, propertyValidName)) {
					(pResultObject as any)[propertyValidName] = this.validateCheckBox((pObjectForValidation as any)[key as string]);
				}
			} else {
				continue;
			}
		}

		return pResultObject;
	}

	public validateAtLeastOneSelected<T>(pObjectForValidation: T, pInclude: Array<keyof T> = [], pExclude: Array<keyof T> = []): boolean {
		const validateObjKeys = (pInclude.length > 0) ? pInclude : Object.keys(pObjectForValidation) as Array<keyof T>;

		for (let key of validateObjKeys) {
			// Pokud je properta vyloučena, tak ji přeskočím
			if (pExclude.length > 0) {
				if (pExclude.filter(f => f === key)[0] !== undefined) continue;
			}
			if ((pObjectForValidation as any)[key as string]) {
				return true;
			}
		}

		return false;
	}

	public validateOnlyOneSelected<T>(pObjectForValidation: T, pInclude: Array<keyof T> = []): boolean {
		const validateObjKeys = (pInclude.length > 0) ? pInclude : Object.keys(pObjectForValidation) as Array<keyof T>;
		let count = 0;

		for (let key of validateObjKeys) {
			if ((pObjectForValidation as any)[key as string]) {
				count++;
			}

			if (count > 1) {
				break;
			}
		}

		return count === 1;
	}

	public validateAtLeastOneFalse<T>(pObjectForValidation: T, pInclude: Array<keyof T> = [], pExclude: Array<keyof T> = []): boolean {
		const validateObjKeys = (pInclude.length > 0) ? pInclude : Object.keys(pObjectForValidation) as Array<keyof T>;

		for (let key of validateObjKeys) {
			// Pokud je properta vyloučena, tak ji přeskočím
			if (pExclude.length > 0) {
				// Poduk je počet vyloučených shodný s počtem propert na kontrolu, tak vrátím true;
				if (pExclude.length === validateObjKeys.length) return false;
				// Jinak přeskočím
				if (pExclude.filter(f => f === key)[0] !== undefined) continue;
			}

			if ((!pObjectForValidation as any)[key as string]) {
				return true;
			}
		}

		return false;
	}

	public validateAtLeastOneNotNull<T>(pObjectForValidation: T, pInclude: Array<keyof T> = [], pExclude: Array<keyof T> = []): boolean {
		const validateObjKeys = (pInclude.length > 0) ? pInclude : Object.keys(pObjectForValidation) as Array<keyof T>;

		for (let key of validateObjKeys) {
			// Pokud je properta vyloučena, tak ji přeskočím
			if (pExclude.length > 0) {
				// Poduk je počet vyloučených shodný s počtem propert na kontrolu, tak vrátím true;
				if (pExclude.length === validateObjKeys.length) return true;
				// Jinak přeskočím
				if (pExclude.filter(f => f === key)[0] !== undefined) continue;
			}

			if ((pObjectForValidation as any)[key as string] != null) {
				return true;
			}
		}

		return false;
	}

	public validateStrings<T, U>(pObjectForValidation: T, pResultObject: U, pExclude: Array<keyof T> = []): U {
		const validateObjKeys = Object.keys(pObjectForValidation) as Array<keyof T>;

		for (let key of validateObjKeys) {
			let propertyType = typeof pObjectForValidation[key];
			let propertyValidName = key + "Valid";

			// Pokud je properta vyloučena, tak ji přeskočím
			if (pExclude.length > 0) {
				if (pExclude.filter(f => f === key)[0] !== undefined) continue;
			}

			if (propertyType === "string") {
				if (Object.prototype.hasOwnProperty.call(pResultObject, propertyValidName)) {
					(pResultObject as any)[propertyValidName] = this.validateString((pObjectForValidation as any)[key as string]);
				}
			} else {
				continue;
			}
		}

		return pResultObject;
	}

	public validateNumbers<T, U>(pObjectForValidation: T, pResultObject: U, pExclude: Array<keyof T> = [], pInclude: Array<keyof T> = [], pZeroIsValid: boolean = true): U {

		const validateObjKeys = (pInclude.length > 0) ? pInclude : Object.keys(pObjectForValidation) as Array<keyof T>;

		for (let key of validateObjKeys) {
			let propertyType = typeof pObjectForValidation[key];
			let propertyValidName = key + "Valid";

			// Pokud je properta vyloučena, tak ji přeskočím
			if (pExclude.length > 0) {
				if (pExclude.filter(f => f === key)[0] !== undefined) continue;
			}

			if (propertyType === "number" || propertyType === "object") {
				if (Object.prototype.hasOwnProperty.call(pResultObject, propertyValidName)) {
					(pResultObject as any)[propertyValidName]  = this.validateNumber((pObjectForValidation as any)[key as string], pZeroIsValid);
				}
			} else {
				continue;
			}
		}

		return pResultObject;
	}

	public setBooleansValue<T>(pObject: T, pSetTo: boolean, pExclude: Array<keyof T> = []): T {
		const objKeys = Object.keys(pObject) as Array<keyof T>;

		for (let key of objKeys) {

			// Pokud je properta vyloučena, tak ji přeskočím
			if (pExclude.length > 0) {
				if (pExclude.filter(f => f === key)[0] !== undefined) continue;
			}

			(pObject as any)[key as string] = pSetTo;
		}

		return pObject;
	}

	/**
		* Kontrola rodného čísla
		* @param input
	*/
	public ValidatePersonalIdentificationNo(pInput: string, pValidate: boolean, pPrevValue: boolean): boolean {
		if (pInput != null && pValidate) {
			var rocnik = parseInt(pInput.substring(0, 2));

			if (pInput.length >= 9) {
				if (pInput.length === 10) {
					if (parseInt(pInput) % 11 !== 0) {
						return false;
					}
				} else if (rocnik > 53) {
					return false;
				}
			}
			else if (pInput !== "") {
				return false;
			}
		}

		if (!pValidate) return pPrevValue
		return true;
	}

	/**
		* Kontrola telefonního čísla
		* @param pInput
	*/
	public ValidatePhoneNumber(pInput: string): boolean {
		if (pInput !== null && pInput !== "") {
			let maskedValue = pInput.replace(/\s/g, '');		// Odstraní mezery z čísla

			if (maskedValue.length > 1 && (maskedValue.startsWith('+420') || maskedValue.startsWith('+421'))) {		// Pokud se jedná o české, nebo slovenské číslo
				maskedValue = maskedValue.replace(/\+/g, '');		// Odstrani povolene znaky

				return maskedValue.length === 12;		// ceske nebo slovenske cislo ma bez + znaku s predvolbou 12 znaku
			}

			return false;
		}

		return true;
	}

	/**
		* Kontrola mezinárodní předvolby telefonního čísla
		* @param pInput
	*/
	public ValidatePhoneInternationalCode(pInput: string): boolean {
		if (pInput !== null && pInput !== "") {
			let maskedValue = pInput.replace(/\s/g, '');		// Odstraní mezery z čísla

			if (maskedValue[0] !== "+") {		// Pokud číslo nezačíná znakem +
				
				return false;
			}

			return true;
		}

		return true;
	}

	/**
		* Kontrola délky telefonního čísla
		* @param pInput
	*/
	public ValidatePhoneNumberLength(pInput: string): boolean {
		if (pInput !== null && pInput !== "") {
			let maskedValue = pInput.replace(/\s/g, '');		// Odstraní mezery z čísla

			if (maskedValue[0] === "+" && maskedValue.length <= 6) {		// Pokud číslo začíná znakem + a jeho délka je menší, nebo rovno 6

				return false;
			}

			return true;
		}

		return true;
	}

	/**
		* Kontrola kontraktačního telefonního čísla
		* @param pInput
	*/
	public ValidateContractPhoneNumber(pTelefon: string, pMail: string): boolean {
		if (pTelefon != null) {
			if ((pTelefon === "" && pMail !== "") || (pTelefon === "" && pMail === "") || pMail == null) {		// Pokud je zadané telefonní číslo, nebo mail

				return false;
			}

			return true;
		}

		return true;
	}

	/**
		* Kontrola mailu
		* @param pInput
	*/
	public ValidateEmail(pInput: string, enableEmptyValue: boolean = false) : boolean {
		if (enableEmptyValue && !pInput) {
			return true;
		}

		if (pInput === null || pInput === "") {
			return false;
		}

		if (pInput.length > 100) {
			return false;
		}

		const regex = /^(([\w\.\-]+)@([\w\-]+\.)+((\w){2,17}))?$/;
		return regex.test(pInput);
	}

	/**
		* Kontrola kontraktačního mailu
		* @param pInput
	*/
	public ValidateContractEmail(pMail: string, pTelefon: string): boolean {
		if (pMail != null) {
			if ((pMail === "" && pTelefon !== "") || (pMail === "" && pTelefon === "") || pTelefon == null) {		// Pokud je zadané telefonní číslo, nebo mail

				return false;
			}

			return true;
		}

		return true;
	}

	/**
	* Kontrola povinneho telefonu
	* @param pInput
*/
	public ValidateMandatoryPhone(pInput: string): boolean {
		if (pInput === "" || pInput == null) {

			return false;
		}

		return true;
	}

	/**
	 * Check if value is not empty (primarily used in forms)
	 * @param pValue 
	 */
	public validateReuired(pValue: any) {
		// empty string
		if (typeof pValue === "string" && !this.validateString((pValue as string).trim())) {
			return false;
		}
		// empty array
		if (Array.isArray(pValue) && (pValue as []).length === 0) {
			return false;
		}
		// empty FileList
		if (pValue instanceof FileList && pValue.length === 0) {
			return false;
		}
		// other values (based on https://262.ecma-international.org/5.1/#sec-9.2)
		if (pValue !== 0 && !pValue) {
			return false;
		}

		return true;
	}

	/**
	 * Check if string matches regex
	 * @param pValue 
	 * @param regex 
	 */
	public validateRegex(pValue: string, regex: RegExp | string, enableEmptyValue: boolean = false): boolean {
		if (enableEmptyValue && !pValue) {
			return true;
		}

		if (typeof pValue !== "string" || pValue === null) {
			return false;
		}

		if (typeof regex === "string") {
			var regexp = new RegExp(regex, 'i');
			return regexp.test(pValue);
		} else {
			return regex.test(pValue);
		}
	}

	/**
	 * Check if given files meet the conditions
	 * @param files List of files
	 * @param count Max. count of files
	 * @param enableEmpty Enable empty FileList (null, undefined etc.) as valid value
	 */
	 public validateFiles (files: FileList, count: number, enableEmpty = true): boolean {
		if (!files || files.length === 0) {
			// file input may not be mandatory
      return enableEmpty ? true : false;
    }

		const fileValidator = inversifyContainer.get<FileValidator>(IocTypes.FileValidator);

		// validate one file
		if (count === 1) {
			return fileValidator.validate(files[0]);
		} 
		// validate multiple files
		else {
			return fileValidator.validateMultiple(files);
		}
  }

	public validateCompanyRegistrationNumber(companyRegistrationNumber: string): boolean {
    // does it have the desired shape?
    if (!/^\d{8}$/.test(companyRegistrationNumber)) {
        return false;
    }

    // checksum
    let a = 0;
    for (let i = 0; i < 7; i++) {
        a += (parseInt(companyRegistrationNumber[i], 10)) * (8 - i);
    }

    a = a % 11;
    let c: number;

    if (a === 0) {
        c = 1;
    } else if (a === 1) {
        c = 0;
    } else {
        c = 11 - a;
    }

    return parseInt(companyRegistrationNumber[7], 10) === c;
	}

	public validateEan(ean: string): boolean {
    if (isNaN(Number(ean)) || ean.includes('E') || ean.length !== 18) {
        return false;
    }

    let sum = 0;

    for (let i = 0; i < 17; i++) {
        let digit = parseInt(ean.charAt(i), 10);

        if (i % 2 === 0) {
            digit *= 3;
        }

        sum += digit;
    }

    let checkDigit = (Math.floor(sum / 10) + 1) * 10 - sum;
    if (checkDigit === 10) {
        checkDigit = 0;
    }

    return parseInt(ean.charAt(17), 10) === checkDigit;
	}

	public validateEic(eic: string): boolean {
    if (eic.length !== 16) {
        return false;
    }

    const eicTable: { [key: string]: number } = {
        '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
        'A': 10, 'B': 11, 'C': 12, 'D': 13, 'E': 14, 'F': 15, 'G': 16, 'H': 17, 'I': 18, 'J': 19,
        'K': 20, 'L': 21, 'M': 22, 'N': 23, 'O': 24, 'P': 25, 'Q': 26, 'R': 27, 'S': 28, 'T': 29,
        'U': 30, 'V': 31, 'W': 32, 'X': 33, 'Y': 34, 'Z': 35, '-': 36
    };

    let sum = 0;
    let weight = 16;

    for (let i = 0; i < 15; i++) {
        const char = eic.charAt(i);
        const value = eicTable[char];
        sum += value * weight;
        weight--;
    }

    const checkValue = 36 - ((sum - 1) % 37);
    const checkChar = Object.keys(eicTable).find(key => eicTable[key] === checkValue);

    return eic.charAt(15) === checkChar;
	}

	public validateTaxId(taxId: string): boolean {
		// Tax ID must start with 'CZ' or 'SK' and have a total length of 10 or 12 characters
		if (!(taxId.startsWith('CZ') || taxId.startsWith('SK')) || (taxId.length !== 10 && taxId.length !== 12)) {
				return false;
		}

		// Remove the 'CZ' or 'SK' prefix
		const numberPart = taxId.slice(2);

		// Check if the rest is a number
		if (!/^\d+$/.test(numberPart)) {
				return false;
		}

		// If Tax ID length is 10 characters, validate as CompanyRegistrationNumber
		if (numberPart.length === 8) {
				return this.validateCompanyRegistrationNumber(numberPart);
		}

		// If Tax ID length is 12 characters, validate as RC
		if (numberPart.length === 10) {
				return this.ValidatePersonalIdentificationNo(numberPart, true, true);
		}

		return false;
	}
}